import React, { useCallback, useMemo } from "react";

import { BasePropsWithChildren } from "../BaseProps";
import { classNames } from "@greeter/util";

import css from "./AspectRatioBox.module.scss";

type AspectRatioTemplate = `${number}:${number}`;
type AspectRatio<Str extends AspectRatioTemplate> = `${Str}`;

type AspectRatio16by9 = AspectRatio<"16:9">;
type AspectRatio4by3 = AspectRatio<"4:3">;
type AspectRatio1by1 = AspectRatio<"1:1">;

export type AspectRatios = AspectRatio1by1 | AspectRatio4by3 | AspectRatio16by9;

export type AspectRatioBoxProps = BasePropsWithChildren & {
  ratio: AspectRatios;
  innerClassName?: string;
}

export const AspectRatioBox: React.FC<AspectRatioBoxProps> = ({
  ratio,
  children,
  className,
  innerClassName,
  ...props
}) => {
  const innerClassNames = useMemo(
    () => classNames(css.Inner, innerClassName),
    [innerClassName]
  );
  const outerClassNames = useCallback(
    (extraClassNames: string) =>
      classNames(css.Outer, className, extraClassNames),
    [className]
  );

  // We have to use css to access pseudo elements. Of course unless we want another dependency.
  // Adding that dependency is very much overkill right now.
  switch (ratio) {
    case "16:9":
      return (
        <div {...props} {...outerClassNames(css.AspectRatio16by9)}>
          <div {...innerClassNames}>{children}</div>
        </div>
      );
    case "4:3":
      return (
        <div {...props} {...outerClassNames(css.AspectRatio4by3)}>
          <div {...innerClassNames}>{children}</div>
        </div>
      );
    case "1:1": // Fallthrough
    default:
      return (
        <div {...props} {...outerClassNames(css.AspectRatio1by1)}>
          <div {...innerClassNames}>{children}</div>
        </div>
      );
  }
};
